import React, { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { v4 as uuidv4 } from 'uuid';

import { getSortingOption } from 'Dashboard/components/ActionItemsSortingHeader/utils';
import ActionItemsSortingHeader from 'Dashboard/components/ActionItemsSortingHeader/ActionItemsSortingHeader';
import { updateSelectedUuids } from 'Shared/actions/selectedUuids';
import { nestByTabAction, nestItemAction } from 'Shared/actions/meetingItems';
import { WorkspaceItem, SelectedSort, ItemType, ItemPosition } from 'Shared/constants/types';
import { getNextOrPrevItem, PositionTypes } from 'Shared/utils/getNextOrPrevItem';

import ActionItemsList from '../ActionItemsWithNestingTable/ActionItemslist/ActionItemslist';
import 'Shared/styles/Animation.scss';

type Props = {
  forbidDrag?: boolean;
  actionItems: Array<WorkspaceItem>;
  selectedSort: SelectedSort;
  withWorkspaceSection?: boolean;
  withDueDateSection?: boolean;
  withOwnerSection?: boolean;
  positionField: ItemPosition;
  setSelectedSort: Function;
  onActionItemDrop?: Function;
  onCreateItem: Function;
};

function ReadOnlyActionItemsWithNestingTable({
  actionItems,
  forbidDrag = true,
  withWorkspaceSection = true,
  withDueDateSection = true,
  withOwnerSection = false,
  positionField,
  selectedSort,
  setSelectedSort,
  onActionItemDrop,
  onCreateItem,
}: Props) {
  const dispatch = useDispatch();

  const handleSelectedItemUpdate = useCallback(
    itemUuid => dispatch(updateSelectedUuids(itemUuid, { isOneSelected: true })),
    [dispatch]
  );

  const handleClickSortOption = useCallback(
    event => {
      const selectedIdSort = event.currentTarget.id;
      setSelectedSort({
        actionItems: selectedIdSort === 'actionItems' ? getSortingOption(selectedSort.actionItems) : 'default',
        owner: selectedIdSort === 'owner' ? getSortingOption(selectedSort.owner) : 'default',
        workspace: selectedIdSort === 'workspace' ? getSortingOption(selectedSort.workspace) : 'default',
        dueDate: selectedIdSort === 'dueDate' ? getSortingOption(selectedSort.dueDate) : 'default',
      });
    },
    [selectedSort, setSelectedSort]
  );

  const handleArrowUpClick = ({ uuid: currentItemUuid }) => {
    const prevItemUuid = getNextOrPrevItem(actionItems, currentItemUuid, PositionTypes.PREV);
    handleSelectedItemUpdate(prevItemUuid);
  };

  const handleArrowDownClick = ({ uuid: currentItemUuid }) => {
    const nextItemUuid = getNextOrPrevItem(actionItems, currentItemUuid, PositionTypes.NEXT);
    handleSelectedItemUpdate(nextItemUuid);
  };

  const handleAddActionItem = useCallback(
    sourceItemUuid => {
      const itemUuid = uuidv4();
      onCreateItem({ sourceItemUuid, itemUuid });
      handleSelectedItemUpdate(itemUuid);
    },
    [handleSelectedItemUpdate, onCreateItem]
  );

  const handleNestingByDrag = useCallback(
    ({ draggedItemUuids, hoveredItemUuid, hoveredItemId }) => {
      dispatch(
        nestItemAction({
          draggedItemUuids,
          parentId: hoveredItemId,
          parentUuid: hoveredItemUuid,
          itemType: ItemType.actionItem,
          positionField,
        })
      );
    },
    [dispatch, positionField]
  );

  const nestItemByTabClick = useCallback(
    uuid => {
      dispatch(
        nestByTabAction({
          uuid,
          itemType: ItemType.actionItem,
          positionField,
        })
      );
    },
    [dispatch, positionField]
  );

  const hasSorting = Boolean(Object.keys(selectedSort).find(option => selectedSort[option] !== 'default'));

  return (
    <ActionItemsSortingHeader
      withWorkspaceSection={withWorkspaceSection}
      withDueDateSection={withDueDateSection}
      withOwnerSection={withOwnerSection}
      selectedSort={selectedSort}
      inUppercase={false}
      onClickSortOption={handleClickSortOption}
    >
      <div className="border-top">
        <ActionItemsList
          isDisabled={true}
          isCompleteToggleDisabled={true}
          forbidDrag={hasSorting || forbidDrag}
          actionItems={actionItems}
          onAddItem={handleAddActionItem}
          onNestByDrag={handleNestingByDrag}
          nestItemByTabClick={nestItemByTabClick}
          onArrowUpClick={handleArrowUpClick}
          onArrowDownClick={handleArrowDownClick}
          withWorkspaceSection={withWorkspaceSection}
          withDueDateSection={withDueDateSection}
          withOwnerSection={withOwnerSection}
          onActionItemDrop={onActionItemDrop}
        />
      </div>
    </ActionItemsSortingHeader>
  );
}

export default ReadOnlyActionItemsWithNestingTable;
